import * as THREE from 'three/webgpu';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  uniform as tslUniform,
  positionLocal,
  normalize,
  smoothstep,
  mix,
  dot,
  pow,
  float,
  max as tslMax,
  texture as textureNode,
  equirectUV,
} from 'three/tsl';

export type SkyDomeOptions = {
  /**
   * Dome radius (m). Must stay comfortably inside the camera's far plane — the
   * dome is the farthest thing drawn, draws first, and never writes depth, so
   * it is the first casualty of any depth-range difference. `src/shaders/
   * clouds.ts` keeps geometry at `far * 0.9` for the same reason.
   */
  radius?: number;
  groundColor?: THREE.ColorRepresentation;
  horizonColor?: THREE.ColorRepresentation;
  zenithColor?: THREE.ColorRepresentation;
  sunColor?: THREE.ColorRepresentation;
  /**
   * Direction the sunlight comes FROM, in the dome's OWN frame (normalized
   * internally). The gradient and sun are computed from local position, so this
   * is worldRoot/sim space — +Z is downrange — not reference space. Keeping it
   * local is what stops the sun sliding every time the tee is re-anchored.
   */
  sunDirection?: THREE.Vector3;
  /** Overall sky brightness multiplier. */
  intensity?: number;
  /**
   * Equirectangular sky image. When supplied it replaces the procedural
   * gradient entirely — the gradient exists because this repo ships no
   * standalone sky asset (course HDRIs arrive embedded in course GLBs).
   */
  texture?: THREE.Texture;
};

/**
 * Sky for the MR range, as a dome mesh rather than a scene background.
 *
 * `SkyBox` (src/sky.ts) sets `scene.backgroundNode`, which paints every pixel
 * of the framebuffer — in passthrough that erases the real room everywhere,
 * portal included. A mesh can instead be fed through `WorldMask`, so the sky
 * obeys the same portal boundary as the turf and the mountains: visible
 * downrange, absent behind the player and directly overhead, where the real
 * ceiling should still show.
 *
 * Uses MeshBasicNodeMaterial specifically: WorldMask.applyToMaterial requires
 * `isNodeMaterial`, which the classic THREE.Mesh*Material classes do not set
 * (and `three/webgpu` re-exports the very same classic classes — only the
 * explicit *NodeMaterial ones qualify).
 */
export class SkyDome {
  readonly object: THREE.Mesh;
  readonly material: MeshBasicNodeMaterial;

  readonly uGroundColor: any;
  readonly uHorizonColor: any;
  readonly uZenithColor: any;
  readonly uSunColor: any;
  readonly uSunDir: any;
  readonly uIntensity: any;
  /**
   * Exponent of the sun disc — higher is tighter. Kept moderate on purpose:
   * pow() is exp2(n · log2(x)), and at very large n mobile Adreno drivers
   * disagree about whether pow(0, n) is 0 or NaN.
   */
  readonly uSunSharpness = tslUniform(250);
  readonly uSunGlow = tslUniform(0.25);
  /** How far up the gradient reaches full zenith colour. */
  readonly uZenithFalloff = tslUniform(0.55);
  /** How far below the horizon the ground haze fades in. */
  readonly uGroundFalloff = tslUniform(0.25);

  constructor(options: SkyDomeOptions = {}) {
    const radius = options.radius ?? 600;

    this.uGroundColor = tslUniform(new THREE.Color(options.groundColor ?? '#5d6f63'));
    this.uHorizonColor = tslUniform(new THREE.Color(options.horizonColor ?? '#cfe0e6'));
    this.uZenithColor = tslUniform(new THREE.Color(options.zenithColor ?? '#4a86c4'));
    this.uSunColor = tslUniform(new THREE.Color(options.sunColor ?? '#fcfae9'));
    this.uIntensity = tslUniform(options.intensity ?? 1);

    const sun = (options.sunDirection ?? new THREE.Vector3(0.35, 0.55, 0.75)).clone().normalize();
    this.uSunDir = tslUniform(sun);

    this.material = new MeshBasicNodeMaterial({
      side: THREE.BackSide,
      // Never occlude anything: the dome draws first and the mountains at
      // z=900 must win regardless of where the dome surface happens to fall.
      depthWrite: false,
      // ...and never BE occluded by the depth buffer either. Combined with
      // renderOrder -1 this takes the dome out of the depth fight entirely,
      // which is what kept it from ever appearing in the headset.
      depthTest: false,
      fog: false,
    });
    this.material.colorNode = options.texture
      ? this.#textureNode(options.texture)
      : this.#gradientNode();

    // Upper hemisphere ONLY. A full sphere fills every direction the nearer
    // geometry doesn't cover, including downward — which lands right on top of
    // the passthrough floor bay and erases the real floor around the mat. The
    // extra 0.02π past the horizon just hides the seam against the ground
    // plane's edge (turf stops at ~250 m laterally, the dome at `radius`).
    const geometry = new THREE.SphereGeometry(radius, 32, 12, 0, Math.PI * 2, 0, Math.PI * 0.52);
    this.object = new THREE.Mesh(geometry, this.material);
    this.object.name = 'SkyDome';
    this.object.frustumCulled = false;
    this.object.renderOrder = -1;
    // Exempt from any future whole-scene mask traversal; the portal mask is
    // applied to this material explicitly by the app instead.
    this.object.userData.mrMaskExempt = true;
  }

  /**
   * Radial direction in the dome's own frame. For a sphere centred on its local
   * origin this IS the sky direction, and unlike `positionWorldDirection` (which
   * is `mat3(modelWorldMatrix) · positionLocal`, so it rotates with the anchor
   * yaw) it is stable across re-anchoring — and needs no camera at all.
   */
  #radialDir() {
    return normalize(positionLocal);
  }

  #textureNode(map: THREE.Texture): any {
    return textureNode(map, equirectUV(this.#radialDir())).mul(this.uIntensity);
  }

  #gradientNode(): any {
    const dir: any = this.#radialDir();
    const height: any = dir.y;

    // Three stops so the below-horizon half reads as distant haze rather than
    // an inverted sky.
    const groundMix: any = smoothstep(this.uGroundFalloff.negate(), float(0), height);
    const zenithMix: any = smoothstep(float(0), this.uZenithFalloff, height);
    let sky: any = mix(this.uGroundColor, this.uHorizonColor, groundMix);
    sky = mix(sky, this.uZenithColor, zenithMix);

    // Clamp the base away from zero before pow(): pow(0, n) is exp2(-inf) and
    // mobile drivers are inconsistent about returning 0 vs NaN, and a NaN here
    // would poison the whole sky colour.
    const facing: any = tslMax(dot(dir, this.uSunDir), float(1e-4));
    const disc: any = pow(facing, this.uSunSharpness);
    const glow: any = pow(facing, float(8)).mul(this.uSunGlow);
    sky = sky.add(this.uSunColor.mul(disc.add(glow)));

    return sky.mul(this.uIntensity);
  }

  /** Point the sun glow at the scene's directional light. */
  setSunDirection(direction: THREE.Vector3) {
    this.uSunDir.value.copy(direction).normalize();
  }

  setIntensity(intensity: number) {
    this.uIntensity.value = intensity;
  }

  dispose() {
    this.object.removeFromParent();
    this.object.geometry.dispose();
    this.material.dispose();
  }
}
